
import { supabase } from '../integrations/supabase/client';

export interface LocationTarget {
  targetUrl: string;
  targetCode: string;
  coordinates: {
    lat: number;
    lng: number;
    heading: number;
  };
}

export class LocationTargetService {
  private readonly MAX_ATTEMPTS = 10;
  private readonly VALIDATION_TIMEOUT = 8000;

  // Location categories with weighted distribution
  private readonly LOCATION_CATEGORIES = {
    URBAN: 'urban',
    SUBURBAN: 'suburban', 
    RURAL: 'rural',
    SCENIC: 'scenic'
  } as const;

  // Weighted distribution: Urban 40%, Suburban 35%, Rural 20%, Scenic 5%
  private readonly CATEGORY_WEIGHTS = [
    { category: this.LOCATION_CATEGORIES.URBAN, weight: 40 },
    { category: this.LOCATION_CATEGORIES.SUBURBAN, weight: 35 },
    { category: this.LOCATION_CATEGORIES.RURAL, weight: 20 },
    { category: this.LOCATION_CATEGORIES.SCENIC, weight: 5 }
  ];

  // Urban locations - major cities with confirmed Street View coverage
  private readonly URBAN_LOCATIONS = [
    // North America (20 cities)
    { lat: 40.7589, lng: -73.9851, name: "New York City, USA" },
    { lat: 37.7749, lng: -122.4194, name: "San Francisco, USA" },
    { lat: 34.0522, lng: -118.2437, name: "Los Angeles, USA" },
    { lat: 41.8781, lng: -87.6298, name: "Chicago, USA" },
    { lat: 25.7617, lng: -80.1918, name: "Miami, USA" },
    { lat: 39.7392, lng: -104.9903, name: "Denver, USA" },
    { lat: 47.6062, lng: -122.3321, name: "Seattle, USA" },
    { lat: 30.2672, lng: -97.7431, name: "Austin, USA" },
    { lat: 43.6532, lng: -79.3832, name: "Toronto, Canada" },
    { lat: 45.5017, lng: -73.5673, name: "Montreal, Canada" },
    { lat: 49.2827, lng: -123.1207, name: "Vancouver, Canada" },
    { lat: 19.4326, lng: -99.1332, name: "Mexico City, Mexico" },
    { lat: 32.7767, lng: -96.7970, name: "Dallas, USA" },
    { lat: 29.7604, lng: -95.3698, name: "Houston, USA" },
    { lat: 33.4484, lng: -112.0740, name: "Phoenix, USA" },
    { lat: 39.9526, lng: -75.1652, name: "Philadelphia, USA" },
    { lat: 42.3601, lng: -71.0589, name: "Boston, USA" },
    { lat: 38.9072, lng: -77.0369, name: "Washington DC, USA" },
    { lat: 35.2271, lng: -80.8431, name: "Charlotte, USA" },
    { lat: 39.7391, lng: -84.2091, name: "Cincinnati, USA" },

    // Europe (25 cities)
    { lat: 51.5074, lng: -0.1278, name: "London, UK" },
    { lat: 48.8566, lng: 2.3522, name: "Paris, France" },
    { lat: 52.5200, lng: 13.4050, name: "Berlin, Germany" },
    { lat: 55.7558, lng: 37.6176, name: "Moscow, Russia" },
    { lat: 40.4168, lng: -3.7038, name: "Madrid, Spain" },
    { lat: 41.9028, lng: 12.4964, name: "Rome, Italy" },
    { lat: 45.4642, lng: 9.1900, name: "Milan, Italy" },
    { lat: 52.3676, lng: 4.9041, name: "Amsterdam, Netherlands" },
    { lat: 50.8503, lng: 4.3517, name: "Brussels, Belgium" },
    { lat: 59.3293, lng: 18.0686, name: "Stockholm, Sweden" },
    { lat: 60.1699, lng: 24.9384, name: "Helsinki, Finland" },
    { lat: 55.6761, lng: 12.5683, name: "Copenhagen, Denmark" },
    { lat: 59.9139, lng: 10.7522, name: "Oslo, Norway" },
    { lat: 47.3769, lng: 8.5417, name: "Zurich, Switzerland" },
    { lat: 48.2082, lng: 16.3738, name: "Vienna, Austria" },
    { lat: 50.0755, lng: 14.4378, name: "Prague, Czech Republic" },
    { lat: 47.4979, lng: 19.0402, name: "Budapest, Hungary" },
    { lat: 52.2297, lng: 21.0122, name: "Warsaw, Poland" },
    { lat: 38.7223, lng: -9.1393, name: "Lisbon, Portugal" },
    { lat: 41.3851, lng: 2.1734, name: "Barcelona, Spain" },
    { lat: 53.3498, lng: -6.2603, name: "Dublin, Ireland" },
    { lat: 55.9533, lng: -3.1883, name: "Edinburgh, Scotland" },
    { lat: 44.4268, lng: 26.1025, name: "Bucharest, Romania" },
    { lat: 42.6977, lng: 23.3219, name: "Sofia, Bulgaria" },
    { lat: 45.8150, lng: 15.9819, name: "Zagreb, Croatia" },

    // Asia (25 cities)
    { lat: 35.6762, lng: 139.6503, name: "Tokyo, Japan" },
    { lat: 34.6937, lng: 135.5023, name: "Osaka, Japan" },
    { lat: 37.5665, lng: 126.9780, name: "Seoul, South Korea" },
    { lat: 39.9042, lng: 116.4074, name: "Beijing, China" },
    { lat: 31.2304, lng: 121.4737, name: "Shanghai, China" },
    { lat: 22.3193, lng: 114.1694, name: "Hong Kong" },
    { lat: 1.3521, lng: 103.8198, name: "Singapore" },
    { lat: 25.0330, lng: 121.5654, name: "Taipei, Taiwan" },
    { lat: 13.7563, lng: 100.5018, name: "Bangkok, Thailand" },
    { lat: 21.0285, lng: 105.8542, name: "Hanoi, Vietnam" },
    { lat: 10.8231, lng: 106.6297, name: "Ho Chi Minh City, Vietnam" },
    { lat: 3.1390, lng: 101.6869, name: "Kuala Lumpur, Malaysia" },
    { lat: -6.2088, lng: 106.8456, name: "Jakarta, Indonesia" },
    { lat: 14.5995, lng: 120.9842, name: "Manila, Philippines" },
    { lat: 28.6139, lng: 77.2090, name: "New Delhi, India" },
    { lat: 19.0760, lng: 72.8777, name: "Mumbai, India" },
    { lat: 12.9716, lng: 77.5946, name: "Bangalore, India" },
    { lat: 41.2995, lng: 69.2401, name: "Tashkent, Uzbekistan" },
    { lat: 43.2220, lng: 76.8512, name: "Almaty, Kazakhstan" },
    // Removed Yerevan, Armenia due to incorrect redirects
    { lat: 41.7151, lng: 44.8271, name: "Tbilisi, Georgia" },
    { lat: 35.6892, lng: 51.3890, name: "Tehran, Iran" },
    { lat: 33.3152, lng: 44.3661, name: "Baghdad, Iraq" },
    { lat: 31.5204, lng: 74.3587, name: "Lahore, Pakistan" },
    { lat: 23.8103, lng: 90.4125, name: "Dhaka, Bangladesh" },

    // Australia & Oceania (8 cities)
    { lat: -33.8688, lng: 151.2093, name: "Sydney, Australia" },
    { lat: -37.8136, lng: 144.9631, name: "Melbourne, Australia" },
    { lat: -27.4698, lng: 153.0251, name: "Brisbane, Australia" },
    { lat: -31.9505, lng: 115.8605, name: "Perth, Australia" },
    { lat: -34.9285, lng: 138.6007, name: "Adelaide, Australia" },
    { lat: -36.8485, lng: 174.7633, name: "Auckland, New Zealand" },
    { lat: -41.2865, lng: 174.7762, name: "Wellington, New Zealand" },
    { lat: -43.5321, lng: 172.6362, name: "Christchurch, New Zealand" },

    // South America (12 cities)
    { lat: -34.6037, lng: -58.3816, name: "Buenos Aires, Argentina" },
    { lat: -23.5505, lng: -46.6333, name: "São Paulo, Brazil" },
    { lat: -22.9068, lng: -43.1729, name: "Rio de Janeiro, Brazil" },
    { lat: -15.7942, lng: -47.8822, name: "Brasília, Brazil" },
    { lat: -33.4489, lng: -70.6693, name: "Santiago, Chile" },
    { lat: 4.7110, lng: -74.0721, name: "Bogotá, Colombia" },
    { lat: -12.0464, lng: -77.0428, name: "Lima, Peru" },
    { lat: 10.4806, lng: -66.9036, name: "Caracas, Venezuela" },
    { lat: -2.1894, lng: -79.8890, name: "Guayaquil, Ecuador" },
    { lat: -25.2637, lng: -57.5759, name: "Asunción, Paraguay" },
    { lat: -34.9011, lng: -56.1645, name: "Montevideo, Uruguay" },
    { lat: -16.5000, lng: -68.1500, name: "La Paz, Bolivia" },

    // Africa & Middle East (10 cities)
    { lat: -26.2041, lng: 28.0473, name: "Johannesburg, South Africa" },
    { lat: -33.9249, lng: 18.4241, name: "Cape Town, South Africa" },
    { lat: 30.0444, lng: 31.2357, name: "Cairo, Egypt" },
    { lat: -1.2921, lng: 36.8219, name: "Nairobi, Kenya" },
    { lat: 33.8938, lng: 35.5018, name: "Beirut, Lebanon" },
    { lat: 31.7683, lng: 35.2137, name: "Jerusalem, Israel" },
    { lat: 32.0853, lng: 34.7818, name: "Tel Aviv, Israel" },
    { lat: 25.2048, lng: 55.2708, name: "Dubai, UAE" },
    { lat: 29.3117, lng: 47.4818, name: "Kuwait City, Kuwait" },
    { lat: 26.8206, lng: 30.8025, name: "Luxor, Egypt" }
  ];

  // Suburban locations - residential areas and smaller towns
  private readonly SUBURBAN_LOCATIONS = [
    // North America Suburbs
    { lat: 37.4419, lng: -122.1430, name: "Palo Alto, CA, USA" },
    { lat: 40.7282, lng: -74.0776, name: "Hoboken, NJ, USA" },
    { lat: 42.3770, lng: -71.1167, name: "Cambridge, MA, USA" },
    { lat: 34.1478, lng: -118.1445, name: "Pasadena, CA, USA" },
    { lat: 25.7907, lng: -80.1300, name: "Coral Gables, FL, USA" },
    { lat: 47.6740, lng: -122.1215, name: "Bellevue, WA, USA" },
    { lat: 39.0458, lng: -76.6413, name: "Columbia, MD, USA" },
    { lat: 45.4215, lng: -75.6919, name: "Ottawa, Canada" },
    { lat: 43.2642, lng: -79.8492, name: "Burlington, ON, Canada" },
    
    // European Suburbs & Towns
    { lat: 51.3806, lng: -0.3356, name: "Richmond, London, UK" },
    { lat: 52.2053, lng: 0.1218, name: "Cambridge, UK" },
    { lat: 48.7833, lng: 2.2167, name: "Versailles, France" },
    { lat: 52.1601, lng: 4.4970, name: "Haarlem, Netherlands" },
    { lat: 55.6050, lng: 13.0038, name: "Malmö, Sweden" },
    { lat: 60.2934, lng: 5.3357, name: "Bergen, Norway" },
    { lat: 46.9479, lng: 7.4474, name: "Bern, Switzerland" },
    
    // Asian Suburbs & Towns
    { lat: 35.6286, lng: 139.8094, name: "Shibuya, Tokyo, Japan" },
    { lat: 37.4563, lng: 126.7052, name: "Incheon, South Korea" },
    { lat: 1.2966, lng: 103.7764, name: "Jurong, Singapore" },
    { lat: 22.2783, lng: 114.1747, name: "Sha Tin, Hong Kong" },
    
    // Australian Suburbs
    { lat: -33.7714, lng: 151.1167, name: "Parramatta, NSW, Australia" },
    { lat: -37.8770, lng: 145.0443, name: "Box Hill, VIC, Australia" }
  ];

  // Rural locations - countryside, small towns, highways
  private readonly RURAL_LOCATIONS = [
    // North America Rural
    { lat: 44.2619, lng: -72.5806, name: "Montpelier, VT, USA" },
    { lat: 46.8059, lng: -100.7837, name: "Bismarck, ND, USA" },
    { lat: 41.5868, lng: -93.6250, name: "Des Moines, IA, USA" },
    { lat: 39.1612, lng: -75.5264, name: "Dover, DE, USA" },
    { lat: 58.3019, lng: -134.4197, name: "Juneau, AK, USA" },
    { lat: 53.5461, lng: -113.4938, name: "Edmonton, AB, Canada" },
    { lat: 49.8951, lng: -97.1384, name: "Winnipeg, MB, Canada" },
    
    // European Rural
    { lat: 64.1466, lng: -21.9426, name: "Reykjavik, Iceland" },
    { lat: 54.3781, lng: -2.9849, name: "Lancaster, UK" },
    { lat: 50.0359, lng: 19.9569, name: "Krakow, Poland" },
    { lat: 46.0569, lng: 14.5058, name: "Ljubljana, Slovenia" },
    { lat: 59.4370, lng: 24.7536, name: "Tallinn, Estonia" },
    
    // Other Rural
    { lat: -42.8821, lng: 147.3272, name: "Hobart, Tasmania, Australia" },
    { lat: -45.8788, lng: 170.5028, name: "Dunedin, New Zealand" },
    { lat: -29.8587, lng: 31.0218, name: "Durban, South Africa" }
  ];

  // Scenic locations - tourist areas, coastal roads, national parks with Street View
  private readonly SCENIC_LOCATIONS = [
    // Coastal & Scenic Routes
    { lat: 36.5545, lng: -121.9234, name: "Big Sur, CA, USA" },
    { lat: 45.3311, lng: -121.7113, name: "Mount Hood, OR, USA" },
    { lat: 40.7829, lng: -73.9654, name: "Central Park, NY, USA" },
    { lat: 51.1789, lng: -1.8262, name: "Stonehenge, UK" },
    { lat: 50.8263, lng: -0.1372, name: "Brighton, UK" },
    { lat: 43.7102, lng: 7.2620, name: "Nice, France" },
    { lat: 46.5197, lng: 6.6323, name: "Montreux, Switzerland" },
    { lat: 35.3606, lng: 138.7274, name: "Mount Fuji, Japan" },
    { lat: -34.3553, lng: 150.8620, name: "Royal National Park, Australia" },
    { lat: 64.9631, lng: -19.0208, name: "Golden Circle, Iceland" }
  ];

  async generateLocationTarget(): Promise<LocationTarget | null> {
    console.log('🗺️ LocationTargetService: Starting enhanced location target generation...');

    let attempts = 0;
    while (attempts < this.MAX_ATTEMPTS) {
      try {
        // Select location category based on weighted distribution
        const selectedCategory = this.selectLocationCategory();
        const baseLocation = this.selectLocationFromCategory(selectedCategory);
        const coordinates = this.addLocationVariation(baseLocation, selectedCategory);
        const heading = Math.floor(Math.random() * 360);

        console.log(`🗺️ LocationTargetService: Attempt ${attempts + 1} - Validating ${selectedCategory} location:`, baseLocation.name);
        
        // Validate Street View availability
        const isValid = await this.validateStreetViewWithTimeout(coordinates.lat, coordinates.lng);
        
        if (isValid) {
          const targetUrl = await this.buildGoogleMapsUrl(coordinates.lat, coordinates.lng, heading);
          const targetCode = this.generateTargetCode();

          console.log(`✅ LocationTargetService: Valid ${selectedCategory} location target generated:`, baseLocation.name);
          console.log('🎯 LocationTargetService: Target URL:', targetUrl);

          return {
            targetUrl,
            targetCode,
            coordinates: { ...coordinates, heading }
          };
        } else {
          console.log(`❌ LocationTargetService: Street View not available for ${baseLocation.name}, trying another location...`);
        }
      } catch (error) {
        console.error(`❌ LocationTargetService: Error during validation attempt ${attempts + 1}:`, error);
      }
      
      attempts++;
    }

    console.error('❌ LocationTargetService: Failed to generate valid location target after max attempts');
    return null;
  }

  private selectLocationCategory(): string {
    const random = Math.random() * 100;
    let cumulativeWeight = 0;
    
    for (const { category, weight } of this.CATEGORY_WEIGHTS) {
      cumulativeWeight += weight;
      if (random <= cumulativeWeight) {
        console.log(`🎯 LocationTargetService: Selected category: ${category} (${weight}% weight)`);
        return category;
      }
    }
    
    // Fallback to urban
    return this.LOCATION_CATEGORIES.URBAN;
  }

  private selectLocationFromCategory(category: string): { lat: number; lng: number; name: string } {
    let locationPool: { lat: number; lng: number; name: string }[];
    
    switch (category) {
      case this.LOCATION_CATEGORIES.SUBURBAN:
        locationPool = this.SUBURBAN_LOCATIONS;
        break;
      case this.LOCATION_CATEGORIES.RURAL:
        locationPool = this.RURAL_LOCATIONS;
        break;
      case this.LOCATION_CATEGORIES.SCENIC:
        locationPool = this.SCENIC_LOCATIONS;
        break;
      case this.LOCATION_CATEGORIES.URBAN:
      default:
        locationPool = this.URBAN_LOCATIONS;
        break;
    }
    
    return locationPool[Math.floor(Math.random() * locationPool.length)];
  }

  private addLocationVariation(
    baseLocation: { lat: number; lng: number }, 
    category: string
  ): { lat: number; lng: number } {
    // Dynamic variation based on location type
    let variation: number;
    
    switch (category) {
      case this.LOCATION_CATEGORIES.RURAL:
        variation = 0.009; // ~1000m for rural areas
        break;
      case this.LOCATION_CATEGORIES.SUBURBAN:
        variation = 0.0045; // ~500m for suburban areas
        break;
      case this.LOCATION_CATEGORIES.SCENIC:
        variation = 0.0027; // ~300m for scenic locations
        break;
      case this.LOCATION_CATEGORIES.URBAN:
      default:
        variation = 0.0018; // ~200m for urban areas
        break;
    }
    
    // Add terrain-aware exclusion for water bodies (simplified)
    let attempts = 0;
    const maxAttempts = 5;
    
    while (attempts < maxAttempts) {
      const lat = baseLocation.lat + (Math.random() - 0.5) * variation;
      const lng = baseLocation.lng + (Math.random() - 0.5) * variation;
      
      // Basic water body exclusion (avoid major ocean areas)
      if (!this.isLikelyWaterBody(lat, lng)) {
        console.log(`🎯 LocationTargetService: Applied ${category} variation: ±${Math.round(variation * 111000)}m`);
        return {
          lat: parseFloat(lat.toFixed(6)),
          lng: parseFloat(lng.toFixed(6))
        };
      }
      attempts++;
    }
    
    // Fallback to minimal variation if water exclusion fails
    const lat = baseLocation.lat + (Math.random() - 0.5) * 0.0009;
    const lng = baseLocation.lng + (Math.random() - 0.5) * 0.0009;
    
    return {
      lat: parseFloat(lat.toFixed(6)),
      lng: parseFloat(lng.toFixed(6))
    };
  }

  private isLikelyWaterBody(lat: number, lng: number): boolean {
    // Simple heuristic to avoid major water bodies
    // This is a basic implementation - could be enhanced with actual water body data
    
    // Atlantic Ocean areas
    if (lat > 10 && lat < 60 && lng > -50 && lng < -10) return true;
    // Pacific Ocean areas  
    if (lat > -40 && lat < 40 && lng > -180 && lng < -120) return true;
    // Indian Ocean areas
    if (lat > -40 && lat < 20 && lng > 40 && lng < 100) return true;
    
    return false;
  }

  private async validateStreetViewWithTimeout(lat: number, lng: number): Promise<boolean> {
    console.log('🗺️ LocationTargetService: Validating Street View at:', lat, lng);
    
    return new Promise(async (resolve, reject) => {
      const timeoutId = setTimeout(() => {
        console.log('⏰ LocationTargetService: Street View validation timed out');
        reject(new Error('Validation timeout'));
      }, this.VALIDATION_TIMEOUT);

      try {
        const { data, error } = await supabase.functions.invoke('validate-street-view', {
          body: { lat, lng }
        });

        clearTimeout(timeoutId);

        if (error) {
          console.error('❌ LocationTargetService: Street View validation error:', error);
          reject(error);
          return;
        }

        const isValid = data?.isValid === true || (typeof data?.isValid === 'string' && data.isValid.length > 0);
        console.log('🗺️ LocationTargetService: Street View validation result:', isValid);
        resolve(isValid);
      } catch (error) {
        clearTimeout(timeoutId);
        console.error('❌ LocationTargetService: Exception during Street View validation:', error);
        reject(error);
      }
    });
  }

  private async buildGoogleMapsUrl(lat: number, lng: number, heading: number): Promise<string> {
    try {
      // Try to get panorama ID for more reliable Street View
      const { data } = await supabase.functions.invoke('validate-street-view', {
        body: { lat, lng }
      });
      
      if (data?.panoId && data.panoId !== '0x0:0x0') {
        // Use validated coordinates from the Street View API for consistency
        const validatedLat = data.validatedLat || lat;
        const validatedLng = data.validatedLng || lng;
        
        // Build URL with panorama ID to ensure exact same Street View location
        const url = `https://www.google.com/maps/@${validatedLat},${validatedLng},3a,75y,${heading}t/data=!3m6!1e1!3m4!1s${data.panoId}!2e0!7i16384!8i8192`;
        console.log('🗺️ LocationTargetService: Generated Maps URL with pano ID:', url);
        console.log('🗺️ LocationTargetService: Using validated coordinates:', validatedLat, validatedLng);
        return url;
      }
    } catch (error) {
      console.warn('⚠️ LocationTargetService: Could not get panorama ID, using fallback URL');
    }
    
    // Fallback to standard format - still better than no URL
    const url = `https://www.google.com/maps/@${lat},${lng},3a,75y,${heading}t/data=!3m6!1e1!3m4!1s0x0:0x0!2e0!7i16384!8i8192`;
    console.log('🗺️ LocationTargetService: Generated fallback Maps URL:', url);
    return url;
  }

  private generateTargetCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result.substring(0, 4) + '-' + result.substring(4);
  }
}

export const locationTargetService = new LocationTargetService();
